import React, { useEffect, useState } from 'react'
import styles from './styles.module.css'
import {
  Box,
  Center,
  Circle,
  Float,
  HStack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { useSelector } from 'react-redux'
import { useTranslations } from 'next-intl'
import { createUrlWithParams } from '@/app/[locale]/_utils/createUrlWithParams'
import UserIcon from '@/app/[locale]/_assets/icon-components/User'
import HomeIcon from '@/app/[locale]/_assets/icon-components/Home'
import CartIcon from '@/app/[locale]/_assets/icon-components/Cart'
import { Link, usePathname } from '@/i18n/navigation'
import ReceiptIcon from '@/app/[locale]/_assets/icon-components/Receipt'
import orderService from '@/app/[locale]/_service/orders.service'
import { IRedux } from '@/app/[locale]/_store/types'
import Auth from '@/app/[locale]/_components/Auth'
import { useQuery } from '@tanstack/react-query'

const NavigationMobile = ({ isTgBot = false }: { isTgBot?: boolean }) => {
  const [activeOrderCount, setActiveOrderCount] = useState(0)
  const [optimisticIndex, setOptimisticIndex] = useState<number | null>(null)

  const auth = useSelector((state: IRedux) => state.auth)
  const { cart } = useSelector((state: IRedux) => state.cart)
  const settings = useSelector((state: IRedux) => state.settings.data)
  const colors = useSelector(
    (state: IRedux) => state.settings?.data?.data?.colors
  )
  const { open, onClose, onOpen } = useDisclosure()
  const pathname = usePathname()
  const t = useTranslations()

  const { data, refetch } = useQuery({
    queryKey: ['get-active-order-count'],
    queryFn: () => orderService.getActiveCount().then((res) => res.data),
    enabled: Boolean(auth?.user),
  })

  useEffect(() => {
    if (data?.count) {
      setActiveOrderCount(Number(data?.count))
    }
  }, [data])

  const items = [
    {
      href: '/',
      title: 'home',
      icon: (color: string) => <HomeIcon color={color} />,
    },
    {
      href: `/cart`,
      title: 'cart',
      icon: (color: string) => <CartIcon color={color} />,
    },
    ...(!isTgBot
      ? [
          {
            href: '/orders',
            title: 'orders',
            icon: (color: string) => <ReceiptIcon color={color} />,
            requiresAuth: true,
          },
          {
            href: '/profile',
            title: 'profile',
            icon: (color: string) => <UserIcon color={color} />,
          },
        ]
      : []),
  ]

  // Derive active index from current pathname
  const pathnameBasedIndex = items.findIndex((item) => {
    if (item.href === '/') return pathname === '/'
    return pathname.startsWith(item.href)
  })

  // Use optimistic index for instant feedback, fall back to pathname-based
  const activeIndex =
    optimisticIndex !== null ? optimisticIndex : pathnameBasedIndex

  // Sync optimistic state with actual pathname changes
  useEffect(() => {
    setOptimisticIndex(null)
  }, [pathname])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    if (auth?.user) {
      refetch()
      timeoutId = setTimeout(() => {
        refetch()
      }, 10000)
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [pathname, auth?.user])

  return (
    <>
      <nav className={styles.nav}>
        <div className={styles.waveWrap}>
          <svg
            version='1.1'
            id='wave'
            xmlns='http://www.w3.org/2000/svg'
            xmlnsXlink='http://www.w3.org/1999/xlink'
            viewBox='0 0 119 26'
            style={{
              width: isTgBot ? 'calc(100vw / 2)' : 'calc(100vw / 4)',
              left: `${activeIndex * 25}%`,
            }}
          >
            <path
              className={styles.path}
              d='M120.8,26C98.1,26,86.4,0,60.4,0C35.9,0,21.1,26,0.5,26H120.8z'
            />
          </svg>
        </div>
        <HStack
          roundedTop='15px'
          bgColor='background.card'
          height='70px'
          gap={0}
          boxShadow={!settings?.data?.is_home_page_delivery ? '0px -8px 30px 0px #00000014' : 'none'}
        >
          <Center
            zIndex={1}
            position='absolute'
            width={isTgBot ? 'calc(100vw / 2)' : 'calc(100vw / 4)'}
            transition='all 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
            top='23px'
            left={`${activeIndex * 25}vw`}
          >
            <Box
              minHeight='40px'
              minWidth='40px'
              rounded='full'
              bgColor='primary'
            ></Box>
          </Center>
          {items.map((item, index) => {
            const isActive = index === activeIndex
            const requiresAuth = 'requiresAuth' in item && item.requiresAuth
            const shouldPreventDefault = requiresAuth && !auth?.user

            return (
              <Center
                key={item.title + index}
                flex={1}
                fontSize='xl'
                flexDir='column'
                zIndex={1}
                transform={`translateY(${isActive ? '-15' : '0'}px)`}
                transition='all 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
              >
                <Link
                  href={createUrlWithParams(item.href)}
                  onClick={(e) => {
                    if (shouldPreventDefault) {
                      e.preventDefault()
                      onOpen()
                    } else {
                      // Instantly update UI for smooth feedback
                      setOptimisticIndex(index)
                    }
                  }}
                  prefetch={true}
                >
                  <Box position='relative'>
                    {item.title === 'cart' && cart?.length > 0 && (
                      <Float top={0} right={0}>
                        <Circle
                          size='4'
                          bg='primary'
                          fontSize='9px'
                          color='white'
                        >
                          {cart.length}
                        </Circle>
                      </Float>
                    )}
                    {item.title === 'orders' && activeOrderCount > 0 && (
                      <Float top={0} right={3}>
                        <Circle
                          size='4'
                          bg={'primary'}
                          fontSize='9px'
                          color={'white'}
                        >
                          {activeOrderCount}
                        </Circle>
                      </Float>
                    )}
                    <Center
                      minHeight={isActive ? '40px' : '24px'}
                      minWidth={isActive ? '40px' : '24px'}
                      rounded='full'
                      transition='all 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
                    >
                      {item.icon(
                        isActive
                          ? colors?.is_advanced
                            ? 'var(--product-btn-text)'
                            : 'white'
                          : 'var(--text-muted)'
                      )}
                    </Center>
                    <Text
                      textAlign='center'
                      fontSize='2xs'
                      color={isActive ? 'primary' : 'text.muted'}
                    >
                      {t(item.title)}
                    </Text>
                  </Box>
                </Link>
              </Center>
            )
          })}
        </HStack>
      </nav>
      <Auth open={open} onClose={onClose} />
    </>
  )
}

export default NavigationMobile
